/**
 * Display formatting.
 *
 * D-04: timestamps are stored as UTC and displayed in West Africa Time. All
 * conversion happens here so no component has to think about it.
 */

const WAT = "Africa/Lagos";

const dateTimeFormat = new Intl.DateTimeFormat("en-GB", {
  timeZone: WAT,
  day: "2-digit",
  month: "short",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
  hour12: false,
});

const timeFormat = new Intl.DateTimeFormat("en-GB", {
  timeZone: WAT,
  hour: "2-digit",
  minute: "2-digit",
  hour12: false,
});

const dateFormat = new Intl.DateTimeFormat("en-GB", {
  timeZone: WAT,
  day: "2-digit",
  month: "short",
  year: "numeric",
});

/** "14 Mar 2026 09:15" in WAT. */
export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return "—";
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return "—";
  return dateTimeFormat.format(parsed);
}

export function formatTime(iso: string | null | undefined): string {
  if (!iso) return "—";
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return "—";
  return timeFormat.format(parsed);
}

/**
 * A plain `date` column carries no timezone, so it is formatted from its
 * parts rather than through a Date that would shift it (docs/06 §6.5).
 */
export function formatDate(value: string | null | undefined): string {
  if (!value) return "—";
  const parts = value.split("-");
  if (parts.length === 3) {
    const [year, month, day] = parts;
    const parsed = new Date(Date.UTC(Number(year), Number(month) - 1, Number(day)));
    if (!Number.isNaN(parsed.getTime())) return dateFormat.format(parsed);
  }
  return value;
}

/** "3 minutes ago", for activity feeds. */
export function formatRelative(iso: string | null | undefined): string {
  if (!iso) return "—";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "—";

  const seconds = Math.round((Date.now() - then) / 1000);
  if (seconds < 60) return "just now";
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? "" : "s"} ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? "" : "s"} ago`;
  const days = Math.round(hours / 24);
  if (days < 30) return `${days} day${days === 1 ? "" : "s"} ago`;
  return formatDate(iso.slice(0, 10));
}

/**
 * dd/mm/yyyy as typed on the register, to the ISO form the API expects (E-02).
 * Returns null when the date is impossible, so the caller can reject it.
 */
export function parseRegisterDate(input: string): string | null {
  const text = input.trim();
  if (!text) return null;

  const match =
    /^(\d{1,2})[/\-.](\d{1,2})[/\-.](\d{4})$/.exec(text) ??
    /^(\d{4})-(\d{2})-(\d{2})$/.exec(text);
  if (!match) return null;

  let day: number, month: number, year: number;
  if (text.includes("/") || /^\d{1,2}[-.]/.test(text)) {
    day = Number(match[1]);
    month = Number(match[2]);
    year = Number(match[3]);
  } else {
    year = Number(match[1]);
    month = Number(match[2]);
    day = Number(match[3]);
  }

  const candidate = new Date(Date.UTC(year, month - 1, day));
  // Round-trip check rejects 31/02 rather than rolling it into March.
  if (
    candidate.getUTCFullYear() !== year ||
    candidate.getUTCMonth() !== month - 1 ||
    candidate.getUTCDate() !== day
  ) {
    return null;
  }

  return `${year.toString().padStart(4, "0")}-${month
    .toString()
    .padStart(2, "0")}-${day.toString().padStart(2, "0")}`;
}

/** ISO date to dd/mm/yyyy for an input the volunteer types into. */
export function toRegisterDate(iso: string | null | undefined): string {
  if (!iso) return "";
  const parts = iso.split("-");
  if (parts.length !== 3) return iso;
  const [y, m, d] = parts;
  return `${d}/${m}/${y}`;
}

export function formatBytes(bytes: number | null | undefined): string {
  if (bytes === null || bytes === undefined) return "—";
  const units = ["B", "KB", "MB", "GB", "TB"];
  let value = bytes;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value.toFixed(unit === 0 ? 0 : 1)} ${units[unit]}`;
}
